<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import PageHeader from '../../SettingsSubPageHeader.vue';
import personalMessengerConnections from 'dashboard/api/personalMessengerConnections';

const props = defineProps({
  provider: { type: String, required: true },
  title: { type: String, required: true },
  description: { type: String, required: true },
  authMode: { type: String, required: true },
  embedded: { type: Boolean, default: false },
});

const connections = ref([]);
const route = useRoute();
const selectedId = ref(null);
const inboxName = ref('');
const phoneNumber = ref('');
const verificationCode = ref('');
const accountPassword = ref('');
const qr = ref(null);
const error = ref('');
const isLoading = ref(false);
let poller = null;

const selected = computed(() =>
  connections.value.find(item => item.id === selectedId.value)
);

const needsQr = computed(
  () => props.authMode === 'qr' && selected.value?.status !== 'connected'
);
const needsCode = computed(() => selected.value?.status === 'waiting_code');
const needsPassword = computed(
  () => selected.value?.status === 'waiting_password'
);
const qrSrc = computed(() => {
  const value = qr.value?.qr_png_base64;
  if (!value) return '';
  return value.startsWith('data:') ? value : `data:image/png;base64,${value}`;
});

const statusClass = status => {
  if (status === 'connected') return 'bg-n-teal-3 text-n-teal-11';
  if (['error', 'logged_out'].includes(status))
    return 'bg-n-ruby-3 text-n-ruby-11';
  return 'bg-n-amber-3 text-n-amber-11';
};

const statusLabel = status =>
  ({
    waiting_qr: 'Ожидает QR',
    waiting_code: 'Ожидает код',
    waiting_password: 'Ожидает пароль',
    connecting: 'Подключение',
    connected: 'Подключён',
    disconnected: 'Отключён',
    reconnecting: 'Переподключение',
    logged_out: 'Сессия удалена',
    error: 'Ошибка',
  })[status] || status;

const setError = exception => {
  error.value =
    exception?.response?.data?.error ||
    exception?.message ||
    'Не удалось выполнить запрос к gateway';
};

const loadConnections = async () => {
  try {
    const { data } = await personalMessengerConnections.list();
    connections.value = data.filter(item => item.provider === props.provider);
    if (!selectedId.value && connections.value.length) {
      const requested = connections.value.find(
        item => String(item.id) === String(route.query.connection_id)
      );
      selectedId.value = (requested || connections.value[0]).id;
    }
  } catch (exception) {
    setError(exception);
  }
};

const replaceConnection = connection => {
  const index = connections.value.findIndex(item => item.id === connection.id);
  if (index === -1) connections.value.unshift(connection);
  else connections.value.splice(index, 1, connection);
};

const selectConnection = async id => {
  selectedId.value = id;
  qr.value = null;
  error.value = '';
  await refreshStatus();
};

const createConnection = async () => {
  if (!inboxName.value.trim()) return;
  isLoading.value = true;
  error.value = '';
  try {
    const { data } = await personalMessengerConnections.create({
      provider: props.provider,
      name: inboxName.value.trim(),
    });
    replaceConnection(data);
    selectedId.value = data.id;
    inboxName.value = '';
    await startConnection();
  } catch (exception) {
    setError(exception);
  } finally {
    isLoading.value = false;
  }
};

const startConnection = async () => {
  if (!selected.value) return;
  isLoading.value = true;
  error.value = '';
  try {
    const payload = props.authMode === 'phone' ? { phone_number: phoneNumber.value } : { method: 'qr' };
    const { data } = await personalMessengerConnections.connect(selected.value.id, payload);
    replaceConnection(data);
    if (props.authMode === 'qr') await refreshQr();
  } catch (exception) {
    setError(exception);
  } finally {
    isLoading.value = false;
  }
};

const refreshStatus = async () => {
  if (!selected.value) return;
  try {
    const { data } = await personalMessengerConnections.status(selected.value.id);
    replaceConnection(data);
    if (needsQr.value && !qr.value) await refreshQr();
  } catch (exception) {
    setError(exception);
  }
};

const refreshQr = async () => {
  if (!selected.value) return;
  try {
    const { data } = await personalMessengerConnections.qr(selected.value.id);
    qr.value = data;
  } catch (exception) {
    setError(exception);
  }
};

const submitCode = async () => {
  if (!verificationCode.value || !selected.value) return;
  isLoading.value = true;
  try {
    const { data } = await personalMessengerConnections.code(selected.value.id, verificationCode.value);
    replaceConnection(data);
    verificationCode.value = '';
  } catch (exception) {
    setError(exception);
  } finally {
    isLoading.value = false;
  }
};

const submitPassword = async () => {
  if (!accountPassword.value || !selected.value) return;
  isLoading.value = true;
  try {
    const { data } = await personalMessengerConnections.password(selected.value.id, accountPassword.value);
    replaceConnection(data);
    accountPassword.value = '';
  } catch (exception) {
    setError(exception);
  } finally {
    isLoading.value = false;
  }
};

const disconnect = async () => {
  if (!selected.value) return;
  isLoading.value = true;
  try {
    const { data } = await personalMessengerConnections.disconnect(selected.value.id);
    replaceConnection(data);
  } catch (exception) {
    setError(exception);
  } finally {
    isLoading.value = false;
  }
};

const logout = async () => {
  if (!selected.value) return;
  // eslint-disable-next-line no-alert
  if (!window.confirm(`Завершить session выбранного ${props.title} аккаунта?`))
    return;
  isLoading.value = true;
  try {
    const { data } = await personalMessengerConnections.logout(selected.value.id);
    replaceConnection(data);
    qr.value = null;
  } catch (exception) {
    setError(exception);
  } finally {
    isLoading.value = false;
  }
};

const removeConnection = async () => {
  if (!selected.value) return;
  // eslint-disable-next-line no-alert
  if (
    !window.confirm(
      `Удалить подключение «${selected.value.inbox.name}» и его Inbox?`
    )
  )
    return;
  isLoading.value = true;
  error.value = '';
  try {
    const removedId = selected.value.id;
    await personalMessengerConnections.remove(removedId);
    connections.value = connections.value.filter(item => item.id !== removedId);
    selectedId.value = connections.value[0]?.id || null;
    qr.value = null;
  } catch (exception) {
    setError(exception);
  } finally {
    isLoading.value = false;
  }
};

onMounted(async () => {
  await loadConnections();
  await refreshStatus();
  poller = window.setInterval(refreshStatus, 3000);
});

onUnmounted(() => {
  if (poller) window.clearInterval(poller);
});
</script>

<template>
  <div :class="embedded ? 'w-full' : 'h-full w-full p-6 col-span-6 max-w-4xl'">
    <PageHeader v-if="!embedded" :header-title="title" :header-content="description" />

    <div
      v-if="error"
      class="p-4 mb-4 rounded-xl border border-n-ruby-5 bg-n-ruby-3 text-n-ruby-11"
    >
      {{ error }}
    </div>

    <section class="p-4 mb-4 border border-n-weak rounded-xl bg-n-solid-1">
      <h3 class="text-base font-semibold text-n-slate-12 mb-1">
        Добавить аккаунт
      </h3>
      <p class="mb-3 text-sm text-n-slate-11">
        Каждый аккаунт создаётся как отдельный Inbox и хранит собственную
        session.
      </p>
      <div class="flex flex-col sm:flex-row gap-3">
        <input
          v-model="inboxName"
          class="form-input flex-1"
          type="text"
          maxlength="80"
          :placeholder="`Название Inbox — например, ${title} Продажи`"
        />
        <button class="button nice" type="button" :disabled="isLoading || !inboxName.trim()" @click="createConnection">
          Добавить и показать QR
        </button>
      </div>
    </section>

    <section
      v-if="connections.length"
      class="p-4 mb-4 border border-n-weak rounded-xl bg-n-solid-1"
    >
      <h3 class="text-base font-semibold text-n-slate-12 mb-3">
        Подключённые аккаунты
      </h3>
      <div class="flex flex-wrap gap-2">
        <button
          v-for="connection in connections"
          :key="connection.id"
          type="button"
          class="px-3 py-2 rounded-md border text-left"
          :class="connection.id === selectedId ? 'border-woot-500 bg-n-alpha-2' : 'border-n-weak bg-n-solid-1'"
          @click="selectConnection(connection.id)"
        >
          <span class="block font-medium">{{ connection.inbox.name }}</span>
          <span class="text-xs text-n-slate-11">{{ connection.display_name || statusLabel(connection.status) }}</span>
        </button>
      </div>
    </section>

    <section
      v-if="selected"
      class="p-4 border border-n-weak rounded-xl bg-n-solid-1"
    >
      <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h3 class="text-base font-semibold text-n-slate-12">
            {{ selected.inbox.name }}
          </h3>
          <p class="text-sm text-n-slate-11">
            Inbox ID: {{ selected.inbox.id }} · отдельное устройство
          </p>
        </div>
        <span class="px-3 py-2 rounded-md text-sm font-medium" :class="statusClass(selected.status)">
          {{ statusLabel(selected.status) }}
        </span>
      </div>

      <div v-if="needsQr" class="mb-4">
        <p class="text-sm text-n-slate-11 mb-3">
          Нажмите «Переподключить», если QR ещё не появился.
          <template v-if="provider === 'whatsapp_personal'">
            На телефоне откройте WhatsApp → Настройки (или ⋮) → Связанные
            устройства → Привязать устройство и отсканируйте QR с этого экрана.
          </template>
          <template v-else>
            На телефоне откройте Telegram → Настройки → Устройства →
            Подключить устройство и отсканируйте QR с этого экрана.
            Если запрошен пароль двухэтапной проверки, введите его ниже.
          </template>
          Дождитесь статуса «Подключён». Если открыли ссылку на телефоне,
          откройте её на компьютере для сканирования.
        </p>
        <div class="w-80 max-w-full aspect-square grid place-items-center border border-n-weak rounded-xl bg-white">
          <img v-if="qrSrc" :src="qrSrc" :alt="`${title} login QR`" class="w-72 max-w-[92%] h-auto" />
          <span v-else class="text-sm text-slate-500">QR-код загружается…</span>
        </div>
        <button class="button clear mt-3" type="button" :disabled="isLoading" @click="refreshQr">
          Обновить QR
        </button>
      </div>

      <div v-if="authMode === 'phone' && !needsCode && !needsPassword && selected.status !== 'connected'" class="flex flex-col sm:flex-row gap-3 mb-4">
        <input v-model="phoneNumber" class="form-input flex-1" type="tel" autocomplete="tel" placeholder="+79991234567" />
        <button class="button nice" type="button" :disabled="isLoading || !phoneNumber" @click="startConnection">
          Send code
        </button>
      </div>

      <div v-if="needsCode" class="flex flex-col sm:flex-row gap-3 mb-4">
        <input v-model="verificationCode" class="form-input flex-1" type="text" inputmode="numeric" autocomplete="one-time-code" placeholder="Verification code" />
        <button class="button nice" type="button" :disabled="isLoading || !verificationCode" @click="submitCode">
          Verify code
        </button>
      </div>

      <div v-if="needsPassword" class="flex flex-col sm:flex-row gap-3 mb-4">
        <input v-model="accountPassword" class="form-input flex-1" type="password" autocomplete="current-password" placeholder="Account password" />
        <button class="button nice" type="button" :disabled="isLoading || !accountPassword" @click="submitPassword">
          Verify password
        </button>
      </div>

      <div class="flex flex-wrap gap-2">
        <button class="button clear" type="button" :disabled="isLoading" @click="refreshStatus">Проверить статус</button>
        <button v-if="['disconnected', 'logged_out', 'error'].includes(selected.status) && authMode === 'qr'" class="button nice" type="button" :disabled="isLoading" @click="startConnection">Переподключить</button>
        <button v-if="selected.status === 'connected'" class="button clear" type="button" :disabled="isLoading" @click="disconnect">Приостановить</button>
        <button class="button alert" type="button" :disabled="isLoading" @click="logout">Выйти на устройстве</button>
        <button class="button clear text-red-600" type="button" :disabled="isLoading" @click="removeConnection">Удалить подключение</button>
      </div>

      <dl class="grid sm:grid-cols-2 gap-2 mt-4 text-sm text-n-slate-11">
        <div><dt class="font-medium">Последнее подключение</dt><dd>{{ selected.last_connected_at || '—' }}</dd></div>
        <div><dt class="font-medium">Последнее сообщение</dt><dd>{{ selected.last_message_at || '—' }}</dd></div>
        <div><dt class="font-medium">Последняя ошибка</dt><dd>{{ selected.last_error_code || '—' }}</dd></div>
      </dl>
    </section>
  </div>
</template>
